import type { Pilot, ScryfallCard } from '@edh/shared';
import { accessToken } from './cognito';
import { apiBase } from './config';
import type { LeagueState, Mutation } from './db';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/** Distinguishes "the server said no" from "there is no server right now". */
export class OfflineError extends Error {}

export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let token: string;
  try {
    token = await accessToken();
  } catch (err) {
    // A refresh that fails because the phone is offline must not look like a
    // sign-out, or the app would log everyone out on the walk into the shop.
    if (!navigator.onLine) throw new OfflineError('offline');
    throw err;
  }

  let res: Response;
  try {
    res = await fetch(`${apiBase}${path}`, {
      method,
      headers: {
        authorization: `Bearer ${token}`,
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new OfflineError('network unreachable');
  }

  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(data.error ?? `request failed (${res.status})`, res.status);
  }
  return (await res.json()) as T;
}

export const fetchState = () => request<LeagueState>('GET', '/state');

/** One place that knows how each queued mutation becomes a request. */
export function send(mutation: Mutation): Promise<unknown> {
  switch (mutation.kind) {
    case 'createPilot':
      return request('POST', '/pilots', mutation.payload);
    case 'updatePilot':
      return request('PATCH', `/pilots/${mutation.payload.id}`, mutation.payload);
    case 'createDeck':
      return request('POST', '/decks', mutation.payload);
    case 'updateDeck':
      return request('PATCH', `/decks/${mutation.payload.id}`, mutation.payload);
    case 'deletePilot':
      return request('DELETE', `/pilots/${mutation.payload.id}`);
    case 'deleteDeck':
      return request('DELETE', `/decks/${mutation.payload.id}`);
    case 'logMatch':
      return request('POST', '/matches', mutation.payload);
    case 'undoLast':
      return request('DELETE', '/matches/last');
    case 'deleteMatch':
      return request('DELETE', `/matches/${encodeURIComponent(mutation.payload.matchId)}`);
    case 'editMatch': {
      const { matchId, ...patch } = mutation.payload;
      return request('PATCH', `/matches/${encodeURIComponent(matchId)}`, patch);
    }
    case 'settings':
      return request('PATCH', '/settings', mutation.payload);
  }
}

// ---- Guild identity ---------------------------------------------------------

export interface GuildIdentity {
  guildName: string;
  guildIcon: string | null;
}

/**
 * Unauthenticated, because the sign-in screen needs to show whose guild you are
 * about to join before it has a token to ask with — and a first-time invitee
 * has nothing cached to fall back on.
 */
export async function fetchGuildIdentity(): Promise<GuildIdentity> {
  const res = await fetch(`${apiBase}/guild`);
  if (!res.ok) throw new ApiError(`could not read the guild (${res.status})`, res.status);
  return (await res.json()) as GuildIdentity;
}

// ---- Me ---------------------------------------------------------------------
// Not queued offline: registering is the very first thing a new member does,
// and doing it without a connection would leave them staring at a form that
// appears to have worked while nobody else can see them.

export const registerSelf = (input: { name: string; avatar?: string | null }) =>
  request<Pilot>('POST', '/me', input);

export const updateSelf = (patch: { name?: string; avatar?: string | null }) =>
  request<Pilot>('PATCH', '/me', patch);

export const claimSelf = (pilotId: string) =>
  request<Pilot>('POST', '/me/claim', { pilotId });

/** Leaders only; the server is the one that decides that, not this call. */
export const setLeader = (pilotId: string, leader: boolean) =>
  request<Pilot>('PUT', `/pilots/${pilotId}/role`, { leader });

// ---- Invites ----------------------------------------------------------------
// Not part of the offline mirror: inviting someone is inherently an online act,
// and a queued invite that lands an hour later helps nobody.

export interface Invite {
  email: string;
  invitedAt: number;
  /** Display name, for reading. */
  invitedBy: string;
  /** Cognito subject, for deciding whether you may take it back. */
  invitedBySub: string | null;
  acceptedAt: number | null;
}

export async function listInvites(): Promise<Invite[]> {
  return (await request<{ invites: Invite[] }>('GET', '/invites')).invites;
}

export const addInvite = (email: string) => request<Invite>('POST', '/invites', { email });

export const removeInvite = (email: string) =>
  request<{ removed: string }>('DELETE', `/invites/${encodeURIComponent(email)}`);

// ---- Scryfall (proxied — see packages/api/src/scryfall.ts) -------------------

export async function scryfallAutocomplete(q: string): Promise<string[]> {
  const data = await request<{ data: string[] }>(
    'GET',
    `/scryfall/autocomplete?q=${encodeURIComponent(q)}`,
  );
  return data.data;
}

export const scryfallCard = (name: string) =>
  request<ScryfallCard>('GET', `/scryfall/card?name=${encodeURIComponent(name)}`);

/**
 * The same card by its id, which is what a registered deck stores.
 *
 * By id rather than by name because a name can be ambiguous and an id cannot —
 * and the deck already went through the search once, so the exact printing is
 * known.
 */
export const scryfallCardById = (id: string) =>
  request<ScryfallCard>('GET', `/scryfall/card?id=${encodeURIComponent(id)}`);

/** Art comes back as bytes so it can go straight into the local blob store. */
export async function fetchArt(artCropUrl: string): Promise<Blob> {
  const token = await accessToken();
  const res = await fetch(`${apiBase}/scryfall/art?url=${encodeURIComponent(artCropUrl)}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new ApiError(`art fetch failed (${res.status})`, res.status);
  return res.blob();
}
